// Botón «Cancelar venta» con su modal; cada portal le pasa la mutación de su endpoint.
// La visibilidad (permiso y estado de la venta) la decide quien lo monta.
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Ban } from 'lucide-react';
import type { UseMutationResult } from '@tanstack/react-query';
import { cn } from '@/lib/cn';
import type { SaleLine, SalePromotionInfo } from '@/api/services/retail-wizard/contract';
import type { ReturnPieceCondition } from '@/api/services/retail-wizard/retail-wizard.client';
import { toSlotSale, type SaleSlots } from '@/shared/patterns/SaleReceipt';
import { hasEquipment } from '@/shared/patterns/SaleTicket/ticketSummary';
import { CancelSaleModal } from './CancelSaleModal';
import type { Translate } from './returnConditions';

/** Permiso que habilita cancelar una venta en cualquier portal. */
export const SALE_CANCEL_PERMISSION = 'retail.sale.cancel';

/** Botón rojo de contorno que abre el modal, igual en los dos portales. */
export const CANCEL_SALE_BUTTON =
    'mt-3 inline-flex items-center gap-1 whitespace-nowrap rounded-[5px] border border-destructive/40 bg-card px-2.5 py-1 text-[11px] font-semibold text-destructive transition-colors hover:bg-destructive/10';

/** Lo que viaja a la cancelación; solo lo dañado va en `returnConditions`. */
export interface CancelSaleInput {
    saleId: string;
    reason: string;
    equipmentVerified: boolean;
    returnConditions?: ReturnPieceCondition[];
    /** Excepción LATE_CANCEL aprobada que autoriza cancelar fuera de la ventana. */
    outOfWindowExceptionId?: string;
}

/** Lo que el mensaje de éxito lee de la venta cancelada. */
export interface CancelSaleOutcome {
    creditId: string | null;
    inventoryReturnPending?: boolean;
}

/** Venta que se cancela: lo que el modal y los regalos necesitan de ella. */
export interface CancellableSale extends SalePromotionInfo {
    saleId: string;
    folio: string;
    sku: string | null;
    lines?: SaleLine[];
}

export type CancelSaleMutation<TResult extends CancelSaleOutcome> = Pick<
    UseMutationResult<TResult, Error, CancelSaleInput>,
    'mutate' | 'isPending' | 'error' | 'reset'
>;

/** Qué pasó con la devolución, según lo que respondió la cancelación. */
export function cancelSuccessMessage(result: CancelSaleOutcome, t: Translate): string {
    if (result.inventoryReturnPending !== true) {
        return t('cancelar.exito', {
            defaultValue: 'Venta cancelada. Lo que regresa ya regresó al inventario.',
        });
    }
    if (result.creditId) {
        return t('cancelar.exitoRetenida', {
            defaultValue:
                'Venta cancelada. La devolución al inventario queda retenida hasta que se cancele el crédito y se libere el equipo.',
        });
    }
    return t('cancelar.exitoEnCamino', {
        defaultValue:
            'Venta cancelada. La devolución al inventario va en camino; se aplica en unos minutos.',
    });
}

interface CancelSaleFlowProps<TResult extends CancelSaleOutcome> {
    sale: CancellableSale;
    mutation: CancelSaleMutation<TResult>;
    className?: string;
    /** De aquí sale el nombre de los regalos que solo traen su SKU. */
    slots?: SaleSlots;
    /** El botón nace deshabilitado; `note` explica por qué. */
    disabled?: boolean;
    /** Texto bajo el botón. */
    note?: string;
    /** Viaja en la cancelación cuando la venta salió de la ventana con excepción aprobada. */
    outOfWindowExceptionId?: string;
}

export function CancelSaleFlow<TResult extends CancelSaleOutcome>({
    sale,
    mutation,
    className,
    slots,
    disabled = false,
    note,
    outOfWindowExceptionId,
}: CancelSaleFlowProps<TResult>) {
    const { t } = useTranslation('ventas');
    const [open, setOpen] = useState(false);
    const noteId = useId();

    const giftSlot = slots?.giftName;
    const giftName = giftSlot
        ? (line: SaleLine, lineName: string) =>
              giftSlot(line, toSlotSale(sale, sale.lines ?? []), lineName)
        : undefined;

    const close = () => {
        setOpen(false);
        mutation.reset();
    };

    return (
        <>
            <button
                type="button"
                className={cn(className, 'disabled:cursor-not-allowed disabled:opacity-50')}
                disabled={disabled}
                aria-describedby={note ? noteId : undefined}
                onClick={() => {
                    setOpen(true);
                }}
            >
                <Ban className="size-3" aria-hidden />
                {t('cancelar.titulo', { defaultValue: 'Cancelar venta' })}
            </button>
            {note ? (
                <p id={noteId} className="mt-1 text-xs text-muted-foreground">
                    {note}
                </p>
            ) : null}
            {open && !disabled ? (
                <CancelSaleModal
                    folio={sale.folio}
                    lines={sale.lines}
                    withoutPhone={!hasEquipment(sale)}
                    pending={mutation.isPending}
                    error={mutation.error}
                    {...(giftName ? { giftName } : {})}
                    onClose={close}
                    onConfirm={(reason, equipmentVerified, returnConditions) => {
                        mutation.mutate(
                            {
                                saleId: sale.saleId,
                                reason,
                                equipmentVerified,
                                returnConditions,
                                ...(outOfWindowExceptionId ? { outOfWindowExceptionId } : {}),
                            },
                            {
                                onSuccess: (result) => {
                                    close();
                                    toast.success(cancelSuccessMessage(result, t));
                                },
                            },
                        );
                    }}
                />
            ) : null}
        </>
    );
}
